import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { Helmet } from '@/pages/home/components/SEOHelmet';
import { categories, comics } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新发布' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics = useMemo(() => {
    let result = [...comics];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.includes(q))
      );
    } else if (slug && currentCategory) {
      result = result.filter((c) => c.category === slug);
    }

    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }

    return result;
  }, [slug, searchQuery, sortBy, currentCategory]);

  const totalPages = Math.ceil(filteredComics.length / perPage);
  const pagedComics = filteredComics.slice((currentPage - 1) * perPage, currentPage * perPage);

  const pageTitle = searchQuery
    ? `搜索: ${searchQuery} - 妖精动漫`
    : currentCategory
    ? `${currentCategory.name} - 妖精动漫`
    : '全部分类 - 妖精动漫';

  return (
    <>
      <Helmet
        title={pageTitle}
        description={
          searchQuery
            ? `在妖精动漫搜索"${searchQuery}"，发现你喜欢的韩漫、日漫、国漫，高清免费在线阅读。`
            : currentCategory
            ? `妖精动漫${currentCategory.name}专区，海量${currentCategory.name}资源免费在线阅读，高清画质，每日更新。`
            : '妖精动漫全部分类，韩漫、日漫、国漫、热血、恋爱、悬疑等全类型漫画免费在线看。'
        }
        keywords={
          currentCategory
            ? `妖精动漫${currentCategory.name},${currentCategory.name}在线看,${currentCategory.name}免费阅读`
            : '妖精动漫分类,漫画分类,韩漫,日漫,国漫'
        }
        canonical={`https://www.yaojingdongman.com/category/${slug || 'all'}`}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              {/* Breadcrumb */}
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  {
                    label: searchQuery
                      ? `搜索: ${searchQuery}`
                      : currentCategory?.name || '全部分类',
                  },
                ]}
              />

              {/* Header */}
              <div className="mt-6 mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {searchQuery
                    ? `搜索结果: "${searchQuery}"`
                    : currentCategory
                    ? currentCategory.name
                    : '全部分类'}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  {searchQuery
                    ? `找到 ${filteredComics.length} 部相关漫画`
                    : `共 ${filteredComics.length} 部漫画`}
                </p>
              </div>

              {/* Category Tabs */}
              {!searchQuery && (
                <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                  <Link
                    to="/category/hanman"
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      slug === 'hanman' || !slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </Link>
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        slug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Sort */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value);
                        setCurrentPage(1);
                      }}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                        sortBy === opt.value
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid */}
              {pagedComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {pagedComics.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      author={comic.author}
                      cover={comic.cover}
                      category={comic.category}
                      categoryName={categories.find((c) => c.slug === comic.category)?.name || ''}
                      rating={comic.rating}
                      tags={comic.tags}
                      status={comic.status}
                      size="sm"
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">没有找到相关漫画</p>
                  <p className="text-sm text-foreground-400 mt-1">试试其他关键词或分类吧</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `https://www.yaojingdongman.com/category/${slug || 'all'}#collectionpage`,
            "name": currentCategory ? `${currentCategory.name}漫画` : '全部分类',
            "description": currentCategory
              ? `妖精动漫${currentCategory.name}专区，海量${currentCategory.name}资源免费在线阅读。`
              : '妖精动漫全部分类，韩漫、日漫、国漫等全类型漫画免费在线看。',
            "url": `https://www.yaojingdongman.com/category/${slug || 'all'}`,
            "isPartOf": { "@id": "https://www.yaojingdongman.com/#website" },
            "inLanguage": "zh-CN",
          }),
        }}
      />
    </>
  );
}